<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Settlement & Reconciliation</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Manage hospital settlements and bank reconciliation" name="description" />
    <meta content="CBHI System" name="author" />
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Settlement & Reconciliation</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Finance</a></li>
                    <li class="breadcrumb-item active">Settlements</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Ethiopian/Gregorian Date Filter Section -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card mb-0">
                <div class="card-body py-2">
                  <!-- Row 1: Calendar Toggle + Date Selection -->
                  <div class="row g-2 align-items-center">
                    <!-- Calendar Type Toggle -->
                    <div class="col-auto">
                      <div class="btn-group btn-group-sm" role="group">
                        <input type="radio" class="btn-check" name="calendarType" id="calEth" :value="true" v-model="showEthiopianDates">
                        <label class="btn btn-outline-primary" for="calEth">
                          <i class="bx bx-calendar me-1"></i>Ethiopian
                        </label>
                        <input type="radio" class="btn-check" name="calendarType" id="calGreg" :value="false" v-model="showEthiopianDates">
                        <label class="btn btn-outline-success" for="calGreg">
                          <i class="bx bx-calendar-alt me-1"></i>Gregorian
                        </label>
                      </div>
                    </div>

                    <!-- Date Selection -->
                    <div class="col">
                      <div class="row g-2 align-items-center">
                        <!-- From Date -->
                        <div class="col-auto">
                          <span class="badge bg-secondary">From</span>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                            <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                            <option v-for="d in fromDaysInMonth" :key="d" :value="d">{{ d }}</option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromMonth" @change="onFromMonthChange" class="form-select form-select-sm" style="width: 115px;">
                            <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                            <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                              {{ showEthiopianDates ? m.label : m.labelShort }}
                            </option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                            <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                            <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                          </select>
                        </div>

                        <!-- Separator -->
                        <div class="col-auto">
                          <span class="text-muted">—</span>
                        </div>

                        <!-- To Date -->
                        <div class="col-auto">
                          <span class="badge bg-secondary">To</span>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                            <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                            <option v-for="d in toDaysInMonth" :key="d" :value="d">{{ d }}</option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toMonth" @change="onToMonthChange" class="form-select form-select-sm" style="width: 115px;">
                            <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                            <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                              {{ showEthiopianDates ? m.label : m.labelShort }}
                            </option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                            <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                            <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                          </select>
                        </div>

                        <!-- Action Buttons -->
                        <div class="col-auto">
                          <button type="button" class="btn btn-primary btn-sm" @click="applyDateFilter" :disabled="!isValidDateRange" title="Apply Filter">
                            <i class="bx bx-filter-alt"></i>
                          </button>
                        </div>
                        <div class="col-auto" v-if="hasDateSelection">
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="clearDateFilter" title="Clear Filter">
                            <i class="bx bx-x"></i>
                          </button>
                        </div>

                        <!-- Quick Date Presets -->
                        <div class="col-auto ms-2">
                          <div class="btn-group btn-group-sm" role="group">
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('today')" title="Today">Today</button>
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('week')" title="This Week">Week</button>
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('month')" title="This Month">Month</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: Alternate Calendar Display -->
                  <div class="row mt-2" v-if="alternateDateDisplay">
                    <div class="col-12">
                      <small :class="showEthiopianDates ? 'text-success' : 'text-primary'">
                        <i class="bx bx-calendar-check me-1"></i>
                        <strong>{{ showEthiopianDates ? 'Gregorian' : 'Ethiopian' }}:</strong>
                        {{ alternateDateDisplay }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Dashboard Statistics -->
          <div class="row">
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Pending Settlements</p>
                      <h5 class="mb-0">{{ statistics.pending_count || 0 }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-warning rounded-circle text-white font-size-20">
                        <i class="bx bx-time-five"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Pending Amount</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.pending_amount || 0) }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                        <i class="bx bx-money"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">This Month Paid</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.month_paid || 0) }}</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="bx bx-check-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card blog-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Reconciliation Rate</p>
                      <h5 class="mb-0">{{ statistics.reconciliation_rate || 0 }}%</h5>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="bx bx-chart"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Nav tabs -->
          <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#settlements" role="tab">
                <span class="d-block d-sm-none"><i class="fas fa-home"></i></span>
                <span class="d-none d-sm-block">Settlement Batches</span>
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#reconciliation" role="tab">
                <span class="d-block d-sm-none"><i class="far fa-user"></i></span>
                <span class="d-none d-sm-block">Bank Reconciliation</span>
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#reports" role="tab">
                <span class="d-block d-sm-none"><i class="far fa-envelope"></i></span>
                <span class="d-none d-sm-block">Financial Reports</span>
              </a>
            </li>
          </ul>

          <!-- Tab panes -->
          <div class="tab-content p-3 text-muted">
            <!-- Settlements Tab -->
            <div class="tab-pane active" id="settlements" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header bg-white d-flex justify-content-between align-items-center w-100 border-bottom-1 pb-3 border-bottom">
                      <!-- Filters -->
                      <div class="me-3 text-nowrap filter-tool-bar-btns">
                        <!-- Status Filter -->
                        <div class="btn-group">
                          <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                            <i class="bx bx-filter font-size-16 me-1 align-middle"></i>
                            {{ filters.status ? getStatusLabel(filters.status) : 'All Status' }}
                          </button>
                          <div class="dropdown-menu">
                            <a class="dropdown-item" @click="filterByStatus('')">All Status</a>
                            <a class="dropdown-item" @click="filterByStatus('pending')">Pending</a>
                            <a class="dropdown-item" @click="filterByStatus('processed')">Processed</a>
                            <a class="dropdown-item" @click="filterByStatus('paid')">Paid</a>
                            <a class="dropdown-item" @click="filterByStatus('cancelled')">Cancelled</a>
                          </div>
                        </div>

                        <!-- Hospital Filter -->
                        <div class="btn-group ms-2">
                          <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                            <i class="bx bx-hospital font-size-16 me-1 align-middle"></i>
                            {{ selectedHospitalName || 'All Hospitals' }}
                          </button>
                          <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                            <a class="dropdown-item" @click="filterByHospital(null, 'All Hospitals')">All Hospitals</a>
                            <div class="dropdown-divider"></div>
                            <a v-for="hospital in hospitals" :key="hospital.hospital_id"
                               class="dropdown-item" @click="filterByHospital(hospital.hospital_id, hospital.hospital_name)">
                              {{ hospital.hospital_name }}
                            </a>
                          </div>
                        </div>

                        <!-- Clear Filters -->
                        <button v-if="hasActiveFilters" type="button" class="btn btn-danger waves-effect ms-2" @click="clearFilters">
                          <i class="bx bx-x font-size-16 me-1 align-middle"></i> Clear
                        </button>
                      </div>

                      <div class="btn-toolbar d-flex justify-content-between align-items-center w-100" role="toolbar">
                        <div class="flex-grow-1">
                          <div class="search-box mb-0 me-3">
                            <div class="position-relative">
                              <input v-model="searchQuery" @input="searchSettlements" type="text"
                                     class="form-control bg-light border-light rounded"
                                     placeholder="Search by batch number, hospital name...">
                              <i class="bx bx-search-alt search-icon"></i>
                            </div>
                          </div>
                        </div>

                        <div class="d-flex">
                          <!-- Actions -->
                          <div class="btn-group me-2">
                            <button type="button" class="btn btn-secondary dropdown-toggle" data-bs-toggle="dropdown">
                              <i class="bx bx-download me-1"></i> Export
                            </button>
                            <div class="dropdown-menu">
                              <a class="dropdown-item" @click="exportData('csv')">
                                <i class="bx bx-file me-2"></i> Export as CSV
                              </a>
                            </div>
                          </div>

                          <!-- Process Payments -->
                          <button @click="showPaymentModal = true" type="button" class="btn btn-success"
                                  :disabled="selectedBatches.length === 0">
                            <i class="bx bx-money"></i> <span class="d-md-inline ms-1">Process Payment</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="card-body">
                      <!-- Loading State -->
                      <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-3">Loading settlements...</p>
                      </div>

                      <!-- No Data State -->
                      <div v-else-if="!loading && settlements.length === 0" class="text-center py-5">
                        <i class="bx bx-file-blank text-muted" style="font-size: 48px;"></i>
                        <p class="mt-3 text-muted">No settlement batches found</p>
                      </div>

                      <!-- Data Table -->
                      <div v-else class="table-responsive">
                        <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                          <thead class="table-light">
                          <tr>
                            <th scope="col">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" @change="toggleSelectAll"
                                       :checked="isAllSelected" :disabled="pendingSettlements.length === 0">
                              </div>
                            </th>
                            <th scope="col">Batch Number</th>
                            <th scope="col">Hospital</th>
                            <th scope="col">Period</th>
                            <th scope="col">Claims</th>
                            <th scope="col">Status</th>
                            <th scope="col" class="text-end">Total Amount</th>
                            <th scope="col" class="text-end">Settled Amount</th>
                            <th scope="col">Payment Info</th>
                            <th scope="col" class="text-center">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="batch in settlements" :key="batch.batch_id">
                            <td>
                              <div class="form-check" v-if="batch.status === 'pending' || batch.status === 'processed'">
                                <input class="form-check-input" type="checkbox"
                                       v-model="selectedBatches" :value="batch.batch_id">
                              </div>
                            </td>
                            <td>
                              <div>
                                <h6 class="mb-0">{{ batch.batch_number }}</h6>
                                <small class="text-muted">Created: {{ formatDateDisplay(batch.created_at) }}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <h6 class="mb-0">{{ batch.hospital_name }}</h6>
                                <small class="text-muted">{{ batch.region_name }}, {{ batch.woreda_name }}</small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div>{{ formatDateDisplay(batch.period_start) }}</div>
                                <div class="text-muted">to {{ formatDateDisplay(batch.period_end) }}</div>
                              </div>
                            </td>
                            <td>
                              <span class="badge bg-info">{{ batch.total_claims }} claims</span>
                            </td>
                            <td>
                              <span :class="getStatusBadge(batch.status)">
                                {{ batch.status }}
                              </span>
                            </td>
                            <td class="text-end fw-bold">ETB {{ formatAmount(batch.total_amount) }}</td>
                            <td class="text-end">
                              <span v-if="batch.settled_amount" class="text-success fw-bold">
                                ETB {{ formatAmount(batch.settled_amount) }}
                              </span>
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                              <div v-if="batch.status === 'paid'">
                                <div class="text-success">{{ formatDateDisplay(batch.settlement_date) }}</div>
                                <small class="text-muted">Ref: {{ batch.bank_reference }}</small>
                              </div>
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td class="text-center">
                              <button @click="viewBatchDetails(batch)" class="btn btn-sm btn-primary" title="View Details">
                                <i class="bx bx-show"></i>
                              </button>
                              <button v-if="batch.status === 'pending'" @click="processBatch(batch)"
                                      class="btn btn-sm btn-success ms-1" title="Process Batch">
                                <i class="bx bx-check-circle"></i>
                              </button>
                              <button v-if="batch.status === 'paid'" @click="downloadReceipt(batch)"
                                      class="btn btn-sm btn-info ms-1" title="Download Receipt">
                                <i class="bx bx-download"></i>
                              </button>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>

                      <!-- Pagination -->
                      <div v-if="totalPages > 1" class="row mt-4">
                        <div class="col-sm-12 col-md-6">
                          <div class="dataTables_info">
                            Showing {{ (currentPage - 1) * limit + 1 }} to {{ Math.min(currentPage * limit, total) }} of {{ total }} entries
                          </div>
                        </div>
                        <div class="col-sm-12 col-md-6">
                          <div class="dataTables_paginate paging_simple_numbers float-end">
                            <ul class="pagination">
                              <li class="paginate_button page-item previous" :class="{'disabled': currentPage === 1}">
                                <a @click="changePage(currentPage - 1)" href="#" class="page-link">Previous</a>
                              </li>
                              <li v-for="page in displayedPages" :key="page" class="paginate_button page-item"
                                  :class="{'active': page === currentPage}">
                                <a @click="changePage(page)" href="#" class="page-link">{{ page }}</a>
                              </li>
                              <li class="paginate_button page-item next" :class="{'disabled': currentPage === totalPages}">
                                <a @click="changePage(currentPage + 1)" href="#" class="page-link">Next</a>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bank Reconciliation Tab -->
            <div class="tab-pane" id="reconciliation" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header bg-white">
                      <div class="d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">Bank Reconciliation</h5>
                        <button @click="openReconciliationModal" class="btn btn-primary">
                          <i class="bx bx-upload me-1"></i> Upload Bank Statement
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <div class="table-responsive">
                        <table class="table align-middle table-hover border table-striped mb-0">
                          <thead class="table-light">
                          <tr>
                            <th>Document Name</th>
                            <th>Period</th>
                            <th>Bank Source</th>
                            <th>Total Records</th>
                            <th>Matched</th>
                            <th>Unmatched</th>
                            <th>Match Rate</th>
                            <th>Status</th>
                            <th>Uploaded By</th>
                            <th>Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="recon in reconciliations" :key="recon.reconciliation_id">
                            <td>{{ recon.document_name }}</td>
                            <td>{{ formatDateDisplay(recon.period) }}</td>
                            <td>{{ recon.bank_source }}</td>
                            <td>{{ recon.total_records }}</td>
                            <td class="text-success">{{ recon.matched_records }}</td>
                            <td class="text-danger">{{ recon.unmatched_records }}</td>
                            <td>
                              <div class="progress" style="height: 20px;">
                                <div class="progress-bar" role="progressbar"
                                     :style="{width: getMatchRate(recon) + '%'}"
                                     :class="getMatchRateClass(recon)">
                                  {{ getMatchRate(recon) }}%
                                </div>
                              </div>
                            </td>
                            <td>
                              <span :class="getReconciliationStatusBadge(recon.status)">
                                {{ recon.status }}
                              </span>
                            </td>
                            <td>{{ recon.uploaded_by }}</td>
                            <td>
                              <button @click="viewReconciliationDetails(recon)" class="btn btn-sm btn-primary">
                                <i class="bx bx-show"></i> Details
                              </button>
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Reports Tab -->
            <div class="tab-pane" id="reports" role="tabpanel">
              <div class="row">
                <!-- Monthly Settlement Summary -->
                <div class="col-lg-6">
                  <div class="card">
                    <div class="card-header">
                      <h5 class="mb-0">Monthly Settlement Summary</h5>
                    </div>
                    <div class="card-body">
                      <canvas id="settlementChart" height="300"></canvas>
                    </div>
                  </div>
                </div>

                <!-- Hospital Payment Distribution -->
                <div class="col-lg-6">
                  <div class="card">
                    <div class="card-header">
                      <h5 class="mb-0">Hospital Payment Distribution</h5>
                    </div>
                    <div class="card-body">
                      <canvas id="hospitalChart" height="300"></canvas>
                    </div>
                  </div>
                </div>

                <!-- Financial Summary Report -->
                <div class="col-12 mt-4">
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h5 class="mb-0">Financial Summary Report</h5>
                      <div class="d-flex gap-2">
                        <button @click="refreshReport" class="btn btn-primary" :disabled="generatingReport">
                          <span v-if="generatingReport" class="spinner-border spinner-border-sm me-2"></span>
                          <i v-else class="bx bx-refresh me-1"></i>
                          {{ generatingReport ? 'Loading...' : 'Refresh' }}
                        </button>
                        <button v-if="reportData" @click="exportReport" class="btn btn-success">
                          <i class="bx bx-download me-1"></i> Export CSV
                        </button>
                      </div>
                    </div>
                    <div class="card-body">
                      <div class="row mb-3">
                        <div class="col-md-3">
                          <div class="mb-3">
                            <label class="form-label">Report Type</label>
                            <select v-model="reportFilters.type" class="form-select" @change="autoGenerateReport">
                              <option value="monthly">Monthly Report</option>
                              <option value="quarterly">Quarterly Report</option>
                              <option value="annual">Annual Report</option>
                            </select>
                          </div>
                        </div>
                        <div class="col-md-3">
                          <div class="mb-3">
                            <label class="form-label">Year</label>
                            <select v-model="reportFilters.year" class="form-select" @change="autoGenerateReport">
                              <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                            </select>
                          </div>
                        </div>
                        <div class="col-md-3" v-if="reportFilters.type === 'monthly'">
                          <div class="mb-3">
                            <label class="form-label">Month</label>
                            <select v-model="reportFilters.month" class="form-select" @change="autoGenerateReport">
                              <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
                            </select>
                          </div>
                        </div>
                        <div class="col-md-3" v-if="reportFilters.type === 'quarterly'">
                          <div class="mb-3">
                            <label class="form-label">Quarter</label>
                            <select v-model="reportFilters.quarter" class="form-select" @change="autoGenerateReport">
                              <option value="1">Q1 (Jan-Mar)</option>
                              <option value="2">Q2 (Apr-Jun)</option>
                              <option value="3">Q3 (Jul-Sep)</option>
                              <option value="4">Q4 (Oct-Dec)</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Report Display Area -->
                      <div v-if="reportData" class="report-content">
                        <div class="text-center mb-4">
                          <h4>{{ reportData.reportTitle }}</h4>
                          <p class="text-muted">Period: {{ formatDateDisplay(reportData.period.start) }} - {{ formatDateDisplay(reportData.period.end) }}</p>
                        </div>

                        <!-- Summary Cards -->
                        <div class="row mb-4">
                          <div class="col-md-3">
                            <div class="card bg-primary text-white">
                              <div class="card-body">
                                <h6 style="color: white">Total Batches</h6>
                                <h3 style="color: white">{{ reportData.summary.total_batches || 0 }}</h3>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="card bg-success text-white">
                              <div class="card-body">
                                <h6 style="color: white">Hospitals Paid</h6>
                                <h3 style="color: white">{{ reportData.summary.hospitals_paid || 0 }}</h3>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="card bg-info text-white">
                              <div class="card-body">
                                <h6 style="color: white">Total Claims</h6>
                                <h3 style="color: white">{{ formatNumber(reportData.summary.total_claims || 0) }}</h3>
                              </div>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="card bg-warning text-white">
                              <div class="card-body">
                                <h6 style="color: white">Total Paid</h6>
                                <h3 style="color: white">{{ formatCurrency(reportData.summary.total_paid || 0) }}</h3>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Regional Breakdown -->
                        <div class="card mb-4" v-if="reportData.regionalBreakdown && reportData.regionalBreakdown.length > 0">
                          <div class="card-header">
                            <h5 class="mb-0">Regional Breakdown</h5>
                          </div>
                          <div class="card-body">
                            <div class="table-responsive">
                              <table class="table table-hover mb-0">
                                <thead>
                                <tr>
                                  <th>Region</th>
                                  <th class="text-center">Batches</th>
                                  <th class="text-center">Hospitals</th>
                                  <th class="text-end">Amount Paid</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="region in reportData.regionalBreakdown" :key="region.region_name">
                                  <td>{{ region.region_name }}</td>
                                  <td class="text-center">{{ region.batches }}</td>
                                  <td class="text-center">{{ region.hospitals }}</td>
                                  <td class="text-end">ETB {{ formatAmount(region.amount_paid) }}</td>
                                </tr>
                                </tbody>
                                <tfoot class="table-light">
                                <tr>
                                  <th>Total</th>
                                  <th class="text-center">{{ getTotalBatches }}</th>
                                  <th class="text-center">{{ getTotalHospitals }}</th>
                                  <th class="text-end">ETB {{ formatAmount(getTotalAmount) }}</th>
                                </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>

                        <!-- Top Hospitals -->
                        <div class="card" v-if="reportData.topHospitals && reportData.topHospitals.length > 0">
                          <div class="card-header">
                            <h5 class="mb-0">Top Hospitals by Payment</h5>
                          </div>
                          <div class="card-body">
                            <div class="table-responsive">
                              <table class="table table-hover mb-0">
                                <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Hospital Name</th>
                                  <th class="text-center">Settlements</th>
                                  <th class="text-end">Total Received</th>
                                  <th class="text-end">Average Settlement</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(hospital, index) in reportData.topHospitals" :key="index">
                                  <td>{{ index + 1 }}</td>
                                  <td>{{ hospital.hospital_name }}</td>
                                  <td class="text-center">{{ hospital.settlement_count }}</td>
                                  <td class="text-end">ETB {{ formatAmount(hospital.total_received) }}</td>
                                  <td class="text-end">ETB {{ formatAmount(hospital.avg_settlement) }}</td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        <!-- Monthly Trend -->
                        <div class="card mt-4" v-if="reportData.trendData && reportData.trendData.length > 0">
                          <div class="card-header">
                            <h5 class="mb-0">Payment Trend</h5>
                          </div>
                          <div class="card-body">
                            <canvas id="trendChart" height="100"></canvas>
                          </div>
                        </div>
                      </div>

                      <!-- No Data Message -->
                      <div v-else-if="!generatingReport" class="text-center py-5">
                        <i class="bx bx-file-blank text-muted" style="font-size: 48px;"></i>
                        <p class="mt-3 text-muted">Loading financial summary...</p>
                      </div>

                      <!-- Loading State -->
                      <div v-else class="text-center py-5">
                        <div class="spinner-border text-primary" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-3 text-muted">Generating report...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modals -->
      <SettlementDetailsModal
          v-if="showDetailsModal"
          :batch="selectedBatch"
          @close="closeDetailsModal"
          @process="processBatch"
          @pay="payBatch"
      />

      <PaymentProcessModal
          v-if="showPaymentModal"
          :batches="selectedBatchesData"
          @close="closePaymentModal"
          @process="processPayment"
      />

      <ReconciliationUploadModal
          :show="showReconciliationModal"
          @close="showReconciliationModal = false"
          @uploaded="onReconciliationUploaded"
      />

      <ReconciliationDetailsModal
          v-if="showReconciliationDetailsModal"
          :reconciliation="selectedReconciliation"
          @close="closeReconciliationDetailsModal"
      />

      <!-- Alerts -->
      <div v-if="alert.success" class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
        <div class="alert alert-success alert-dismissible fade show" role="alert">
          <i class="bx bx-check-circle me-2"></i>
          {{ message }}
          <button type="button" class="btn-close" @click="clearAlerts"></button>
        </div>
      </div>

      <div v-if="alert.failed" class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
        <div class="alert alert-danger alert-dismissible fade show" role="alert">
          <i class="bx bx-error-circle me-2"></i>
          {{ message }}
          <button type="button" class="btn-close" @click="clearAlerts"></button>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import SettlementDetailsModal from "@/components/cbhi-settlement/modals/SettlementDetailsModal.vue";
import PaymentProcessModal from "@/components/cbhi-settlement/modals/PaymentProcessModal.vue";
import ReconciliationUploadModal from "@/components/cbhi-settlement/modals/ReconciliationUploadModal.vue";
import ReconciliationDetailsModal from "@/components/cbhi-settlement/modals/ReconciliationDetailsModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";
import Chart from 'chart.js/auto';

export default {
  name: "SettlementManagement",
  components: { NavigationBar, SettlementDetailsModal, PaymentProcessModal, ReconciliationUploadModal, ReconciliationDetailsModal },
  data() {
    return {
      loading: false,
      showEthiopianDates: true,
      currentEthiopianDate: { year: 2017, month: 3, day: 15, monthName: 'ህዳር', monthNameEn: 'Hidar' },
      dateFilterRange: { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' },
      ethiopianMonths: [
        { value: 1, label: 'መስከረም', labelEn: 'Meskerem' }, { value: 2, label: 'ጥቅምት', labelEn: 'Tikimt' },
        { value: 3, label: 'ህዳር', labelEn: 'Hidar' }, { value: 4, label: 'ታህሳስ', labelEn: 'Tahsas' },
        { value: 5, label: 'ጥር', labelEn: 'Tir' }, { value: 6, label: 'የካቲት', labelEn: 'Yekatit' },
        { value: 7, label: 'መጋቢት', labelEn: 'Megabit' }, { value: 8, label: 'ሚያዝያ', labelEn: 'Miyazya' },
        { value: 9, label: 'ግንቦት', labelEn: 'Ginbot' }, { value: 10, label: 'ሰኔ', labelEn: 'Sene' },
        { value: 11, label: 'ሐምሌ', labelEn: 'Hamle' }, { value: 12, label: 'ነሐሴ', labelEn: 'Nehase' },
        { value: 13, label: 'ጳጉሜ', labelEn: 'Pagume' }
      ],
      gregorianMonths: [
        { value: 1, label: 'January', labelShort: 'Jan' }, { value: 2, label: 'February', labelShort: 'Feb' },
        { value: 3, label: 'March', labelShort: 'Mar' }, { value: 4, label: 'April', labelShort: 'Apr' },
        { value: 5, label: 'May', labelShort: 'May' }, { value: 6, label: 'June', labelShort: 'Jun' },
        { value: 7, label: 'July', labelShort: 'Jul' }, { value: 8, label: 'August', labelShort: 'Aug' },
        { value: 9, label: 'September', labelShort: 'Sep' }, { value: 10, label: 'October', labelShort: 'Oct' },
        { value: 11, label: 'November', labelShort: 'Nov' }, { value: 12, label: 'December', labelShort: 'Dec' }
      ],
      settlements: [], reconciliations: [], hospitals: [], selectedBatch: null, selectedBatches: [], selectedBatchesData: [], selectedReconciliation: null,
      statistics: { pending_count: 0, pending_amount: 0, month_paid: 0, reconciliation_rate: 0 },
      filters: { status: '', hospitalId: null, startDate: '', endDate: '' },
      selectedHospitalName: '', searchQuery: '', searchTimeout: null, currentPage: 1, limit: 50, total: 0,
      showDetailsModal: false, showPaymentModal: false, showReconciliationModal: false, showReconciliationDetailsModal: false,
      reportFilters: { type: 'monthly', year: new Date().getFullYear(), month: new Date().getMonth() + 1, quarter: Math.ceil((new Date().getMonth() + 1) / 3) },
      reportData: null, generatingReport: false, trendChart: null, reportGenerationTimeout: null,
      settlementChart: null, hospitalChart: null, settlementChartData: [], hospitalChartLabels: [], hospitalChartData: [],
      alert: { success: false, info: false, failed: false }, message: ''
    };
  },
  computed: {
    ethiopianYearOptions() { const years = []; for (let y = this.currentEthiopianDate.year - 20; y <= this.currentEthiopianDate.year + 5; y++) years.push(y); return years; },
    gregorianYearOptions() { const currentYear = new Date().getFullYear(); const years = []; for (let y = currentYear - 20; y <= currentYear + 5; y++) years.push(y); return years; },
    displayMonths() { return this.showEthiopianDates ? this.ethiopianMonths : this.gregorianMonths; },
    displayYearOptions() { return this.showEthiopianDates ? this.ethiopianYearOptions : this.gregorianYearOptions; },
    fromDaysInMonth() { return this.showEthiopianDates ? this.getDaysInEthiopianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear) : this.getDaysInGregorianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear); },
    toDaysInMonth() { return this.showEthiopianDates ? this.getDaysInEthiopianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear) : this.getDaysInGregorianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear); },
    hasDateSelection() { return (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) || (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear); },
    isValidDateRange() { return this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear && this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear; },
    alternateDateDisplay() {
      if (!this.isValidDateRange) return '';
      if (this.showEthiopianDates) {
        const fromGreg = this.ethiopianToGregorian(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth), parseInt(this.dateFilterRange.fromDay));
        const toGreg = this.ethiopianToGregorian(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth), parseInt(this.dateFilterRange.toDay));
        if (!fromGreg || !toGreg) return '';
        const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${fmt(fromGreg)} — ${fmt(toGreg)}`;
      } else {
        const fromEth = this.gregorianToEthiopian(new Date(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth) - 1, parseInt(this.dateFilterRange.fromDay)));
        const toEth = this.gregorianToEthiopian(new Date(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth) - 1, parseInt(this.dateFilterRange.toDay)));
        if (!fromEth || !toEth) return '';
        return `${fromEth.monthNameEn} ${fromEth.day}, ${fromEth.year} — ${toEth.monthNameEn} ${toEth.day}, ${toEth.year}`;
      }
    },
    totalPages() { return Math.ceil(this.total / this.limit); },
    displayedPages() { const pages = []; const maxVisible = 5; let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2)); let end = Math.min(this.totalPages, start + maxVisible - 1); if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1); for (let i = start; i <= end; i++) pages.push(i); return pages; },
    hasActiveFilters() { return this.filters.status || this.filters.hospitalId || this.filters.startDate || this.filters.endDate; },
    isAllSelected() { const sel = this.settlements.filter(b => b.status === 'pending' || b.status === 'processed'); return sel.length > 0 && this.selectedBatches.length === sel.length; },
    pendingSettlements() { return this.settlements.filter(b => b.status === 'pending' || b.status === 'processed'); },
    availableYears() { const cy = new Date().getFullYear(); const years = []; for (let i = cy; i >= cy - 5; i--) years.push(i); return years; },
    months() { return ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']; },
    getTotalBatches() { if (!this.reportData?.regionalBreakdown) return 0; return this.reportData.regionalBreakdown.reduce((s, r) => s + parseInt(r.batches || 0), 0); },
    getTotalHospitals() { if (!this.reportData?.regionalBreakdown) return 0; return this.reportData.regionalBreakdown.reduce((s, r) => s + parseInt(r.hospitals || 0), 0); },
    getTotalAmount() { if (!this.reportData?.regionalBreakdown) return 0; return this.reportData.regionalBreakdown.reduce((s, r) => s + parseFloat(r.amount_paid || 0), 0); }
  },
  watch: { showEthiopianDates() { this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' }; } },
  mounted() {
    this.currentEthiopianDate = this.getCurrentEthiopianDate();
    this.loadSettlements(); this.loadHospitals(); this.loadReconciliations(); this.initializeCharts();
    this.$nextTick(() => { const rt = document.querySelector('a[href="#reports"]'); if (rt) rt.addEventListener('shown.bs.tab', () => { if (!this.reportData && !this.generatingReport) this.generateFinancialReport(); }); });
  },
  methods: {
    checkAuth() { if (!localStorage.getItem("authToken")) router.push('/login'); },
    getDaysInEthiopianMonth(month, year) { if (!month || !year) return 30; const m = parseInt(month), y = parseInt(year); if (m === 13) return (y % 4 === 3) ? 6 : 5; return 30; },
    getDaysInGregorianMonth(month, year) { if (!month || !year) return 31; const m = parseInt(month), y = parseInt(year); if ([1,3,5,7,8,10,12].includes(m)) return 31; if ([4,6,9,11].includes(m)) return 30; if (m === 2) return ((y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0)) ? 29 : 28; return 31; },
    ethiopianToGregorian(ethYear, ethMonth, ethDay) { const EPOCH = 1724221; const jdn = EPOCH + 365 * (ethYear - 1) + Math.floor(ethYear / 4) + 30 * (ethMonth - 1) + ethDay - 1; const a = jdn + 32044; const b = Math.floor((4 * a + 3) / 146097); const c = a - Math.floor(146097 * b / 4); const d = Math.floor((4 * c + 3) / 1461); const e = c - Math.floor(1461 * d / 4); const m = Math.floor((5 * e + 2) / 153); return new Date(100 * b + d - 4800 + Math.floor(m / 10), m + 3 - 12 * Math.floor(m / 10) - 1, e - Math.floor((153 * m + 2) / 5) + 1); },
    gregorianToEthiopian(gregDate) { const date = typeof gregDate === 'string' ? new Date(gregDate) : gregDate; if (!date || isNaN(date.getTime())) return null; const gYear = date.getFullYear(), gMonth = date.getMonth() + 1, gDay = date.getDate(); const a = Math.floor((14 - gMonth) / 12); const y = gYear + 4800 - a; const m = gMonth + 12 * a - 3; const jdn = gDay + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045; const EPOCH = 1724221; const r = (jdn - EPOCH) % 1461; const n = r % 365 + 365 * Math.floor(r / 1460); const ethYear = 4 * Math.floor((jdn - EPOCH) / 1461) + Math.floor(r / 365) - Math.floor(r / 1460) + 1; const ethMonth = Math.floor(n / 30) + 1; const ethDay = n % 30 + 1; const monthData = this.ethiopianMonths.find(mon => mon.value === ethMonth); return { year: ethYear, month: ethMonth, day: ethDay, monthName: monthData?.label || '', monthNameEn: monthData?.labelEn || '' }; },
    getCurrentEthiopianDate() { return this.gregorianToEthiopian(new Date()); },
    formatDateDisplay(dateString) { if (!dateString) return '-'; if (this.showEthiopianDates) { const eth = this.gregorianToEthiopian(dateString); if (eth) return `${eth.monthNameEn} ${eth.day}, ${eth.year}`; } return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); },
    onFromMonthChange() { if (this.dateFilterRange.fromDay > this.fromDaysInMonth) this.dateFilterRange.fromDay = this.fromDaysInMonth; this.updateDateRange(); },
    onToMonthChange() { if (this.dateFilterRange.toDay > this.toDaysInMonth) this.dateFilterRange.toDay = this.toDaysInMonth; this.updateDateRange(); },
    updateDateRange() {
      if (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) {
        let fromGreg = this.showEthiopianDates ? this.ethiopianToGregorian(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth), parseInt(this.dateFilterRange.fromDay)) : new Date(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth) - 1, parseInt(this.dateFilterRange.fromDay));
        if (fromGreg) this.filters.startDate = fromGreg.toISOString().split('T')[0];
      }
      if (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear) {
        let toGreg = this.showEthiopianDates ? this.ethiopianToGregorian(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth), parseInt(this.dateFilterRange.toDay)) : new Date(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth) - 1, parseInt(this.dateFilterRange.toDay));
        if (toGreg) this.filters.endDate = toGreg.toISOString().split('T')[0];
      }
    },
    applyDateFilter() { if (this.isValidDateRange) { this.updateDateRange(); this.currentPage = 1; this.loadSettlements(); } },
    clearDateFilter() { this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' }; this.filters.startDate = ''; this.filters.endDate = ''; this.currentPage = 1; this.loadSettlements(); },
    setQuickDateRange(period) {
      const today = new Date(); let startDate = new Date(), endDate = new Date();
      switch(period) { case 'today': startDate = new Date(); break; case 'week': startDate.setDate(today.getDate() - 7); break; case 'month': startDate.setDate(1); break; }
      this.filters.startDate = startDate.toISOString().split('T')[0]; this.filters.endDate = endDate.toISOString().split('T')[0];
      if (this.showEthiopianDates) { const sE = this.gregorianToEthiopian(startDate), eE = this.gregorianToEthiopian(endDate); if (sE && eE) this.dateFilterRange = { fromDay: sE.day, fromMonth: sE.month, fromYear: sE.year, toDay: eE.day, toMonth: eE.month, toYear: eE.year }; }
      else this.dateFilterRange = { fromDay: startDate.getDate(), fromMonth: startDate.getMonth() + 1, fromYear: startDate.getFullYear(), toDay: endDate.getDate(), toMonth: endDate.getMonth() + 1, toYear: endDate.getFullYear() };
      this.currentPage = 1; this.loadSettlements();
    },
    async loadSettlements() { this.loading = true; try { const res = await executeJson({ "function": "getSettlementBatches", "status": this.filters.status, "hospitalId": this.filters.hospitalId, "startDate": this.filters.startDate, "endDate": this.filters.endDate, "searchTerm": this.searchQuery, "limit": this.limit, "offset": (this.currentPage - 1) * this.limit }, authUrl); if (res.data.success) { this.settlements = res.data.data.batches || []; this.statistics = { pending_count: 0, pending_amount: 0, month_paid: 0, reconciliation_rate: 0, ...res.data.data.statistics }; this.total = res.data.data.total || 0; } } catch (e) { console.error("Failed to load settlements:", e); this.showAlert('failed', 'Failed to load settlements'); } finally { this.loading = false; } },
    async loadHospitals() { try { const res = await executeJson({ "function": "getHospitalsList", "limit": 1000 }, authUrl); if (res.data.success) this.hospitals = res.data.data || []; } catch (e) { console.error("Failed to load hospitals:", e); } },
    async loadReconciliations() { try { const res = await executeJson({ "function": "getBankReconciliations", "limit": 10 }, authUrl); if (res.data.success && res.data.data) this.reconciliations = res.data.data.reconciliations || []; } catch (e) { console.error("Failed to load reconciliations:", e); this.reconciliations = []; } },
    async viewBatchDetails(batch) { this.selectedBatch = batch; this.showDetailsModal = true; },
    closeDetailsModal() { this.showDetailsModal = false; this.selectedBatch = null; },
    async processBatch(batch) { if (batch.status !== 'pending') return; try { const res = await executeJson({ "function": "processSettlementBatch", "batchId": batch.batch_id }, authUrl); if (res.data.success) { this.showAlert('success', 'Settlement batch processed successfully'); await this.loadSettlements(); } else this.showAlert('failed', res.data.message || 'Failed to process batch'); } catch (e) { console.error("Failed to process batch:", e); this.showAlert('failed', 'Failed to process batch'); } },
    async payBatch(batchId, paymentDetails) { try { const res = await executeJson({ "function": "processSettlementPayment", "batchId": batchId, "bankReference": paymentDetails.bankReference, "settledAmount": paymentDetails.settledAmount }, authUrl); if (res.data.success) { this.showAlert('success', 'Payment processed successfully'); this.closeDetailsModal(); await this.loadSettlements(); } else this.showAlert('failed', res.data.message || 'Failed to process payment'); } catch (e) { console.error("Failed to process payment:", e); this.showAlert('failed', 'Failed to process payment'); } },
    async processPayment(paymentData) { try { const res = await executeJson({ "function": "processBatchPayments", "batchIds": paymentData.batchIds, "bankReference": paymentData.bankReference, "paymentDate": paymentData.paymentDate, "notes": paymentData.notes }, authUrl); if (res.data.success) { this.showAlert('success', 'Payments processed successfully'); this.closePaymentModal(); this.clearSelection(); await this.loadSettlements(); } else this.showAlert('failed', res.data.message || 'Failed to process payments'); } catch (e) { console.error("Failed to process payments:", e); this.showAlert('failed', 'Failed to process payments'); } },
    async downloadReceipt(batch) { try { const res = await executeJson({ "function": "generateSettlementReceipt", "batchId": batch.batch_id }, authUrl); if (res.data.success && res.data.data.receiptUrl) window.open(res.data.data.receiptUrl, '_blank'); else this.showAlert('failed', 'Failed to generate receipt'); } catch (e) { console.error("Failed to download receipt:", e); this.showAlert('failed', 'Failed to download receipt'); } },
    toggleSelectAll() { if (this.isAllSelected) this.selectedBatches = []; else this.selectedBatches = this.settlements.filter(b => b.status === 'pending' || b.status === 'processed').map(b => b.batch_id); },
    clearSelection() { this.selectedBatches = []; },
    filterByStatus(status) { this.filters.status = status; this.currentPage = 1; this.loadSettlements(); },
    filterByHospital(hospitalId, hospitalName) { this.filters.hospitalId = hospitalId; this.selectedHospitalName = hospitalName; this.currentPage = 1; this.loadSettlements(); },
    async clearFilters() { this.filters = { status: '', hospitalId: null, startDate: '', endDate: '' }; this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' }; this.selectedHospitalName = ''; this.searchQuery = ''; this.currentPage = 1; await this.loadSettlements(); },
    async searchSettlements() { this.currentPage = 1; clearTimeout(this.searchTimeout); this.searchTimeout = setTimeout(async () => await this.loadSettlements(), 300); },
    async changePage(page) { if (page < 1 || page > this.totalPages) return; this.currentPage = page; await this.loadSettlements(); },
    viewReconciliationDetails(reconciliation) { this.selectedReconciliation = reconciliation; this.showReconciliationDetailsModal = true; },
    onReconciliationUploaded() { this.showReconciliationModal = false; this.showAlert('success', 'Bank reconciliation uploaded successfully'); this.loadReconciliations(); },
    openReconciliationModal() { this.showReconciliationModal = true; },
    closePaymentModal() { this.showPaymentModal = false; this.selectedBatchesData = []; },
    closeReconciliationDetailsModal() { this.showReconciliationDetailsModal = false; this.selectedReconciliation = null; },
    autoGenerateReport() { if (this.reportGenerationTimeout) clearTimeout(this.reportGenerationTimeout); this.reportGenerationTimeout = setTimeout(() => this.generateFinancialReport(), 500); },
    refreshReport() { this.generateFinancialReport(); },
    async generateFinancialReport() { this.generatingReport = true; this.reportData = null; try { const res = await executeJson({ "function": "generateFinancialReport", "type": this.reportFilters.type, "year": this.reportFilters.year, "month": this.reportFilters.month, "quarter": this.reportFilters.quarter }, authUrl); if (res.data.success && res.data.data) { this.reportData = res.data.data; this.$nextTick(() => { if (this.reportData.trendData?.length > 0) this.initializeTrendChart(); }); } else this.showAlert('failed', res.data.message || 'Failed to generate report'); } catch (e) { console.error("Failed to generate report:", e); this.showAlert('failed', 'Failed to generate report'); } finally { this.generatingReport = false; } },
    async exportData(format) { if (format !== 'csv') return; try { let csv = `Settlement Batches Report\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${this.settlements.length}\n\nSUMMARY STATISTICS\nMetric,Value\nPending Settlements,${this.statistics.pending_count || 0}\nPending Amount,"ETB ${this.formatAmount(this.statistics.pending_amount || 0)}"\nThis Month Paid,"ETB ${this.formatAmount(this.statistics.month_paid || 0)}"\nReconciliation Rate,${this.statistics.reconciliation_rate || 0}%\n\nSETTLEMENT BATCHES\nBatch Number,Hospital,Region,Woreda,Period Start,Period End,Claims,Total Amount,Settled Amount,Settlement Date,Bank Reference,Status,Created Date\n`; this.settlements.forEach(s => { csv += `"${s.batch_number}","${s.hospital_name}","${s.region_name || '-'}","${s.woreda_name || '-'}",${s.period_start},${s.period_end},${s.total_claims},"${this.formatAmount(s.total_amount)}","${s.settled_amount ? this.formatAmount(s.settled_amount) : '-'}",${s.settlement_date || '-'},"${s.bank_reference || '-'}",${s.status},"${s.created_at || '-'}"\n`; }); const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' }); const link = document.createElement('a'); link.download = `settlement_batches_${new Date().toISOString().split('T')[0]}.csv`; link.href = URL.createObjectURL(blob); document.body.appendChild(link); link.click(); document.body.removeChild(link); this.showAlert('success', 'Settlement data exported successfully'); } catch (e) { console.error("Failed to export data:", e); this.showAlert('failed', 'Failed to export data'); } },
    async exportReport() { if (!this.reportData) return; try { let csv = `${this.reportFilters.type.charAt(0).toUpperCase() + this.reportFilters.type.slice(1)} Financial Report\nPeriod: ${this.formatDateDisplay(this.reportData.period.start)} - ${this.formatDateDisplay(this.reportData.period.end)}\n\nSUMMARY\nMetric,Value\nTotal Batches,${this.reportData.summary.total_batches || 0}\nHospitals Paid,${this.reportData.summary.hospitals_paid || 0}\nTotal Claims,${this.reportData.summary.total_claims || 0}\nTotal Amount Paid,${this.reportData.summary.total_paid || 0}\n\n`; if (this.reportData.regionalBreakdown?.length > 0) { csv += 'REGIONAL BREAKDOWN\nRegion,Batches,Hospitals,Amount Paid\n'; this.reportData.regionalBreakdown.forEach(r => csv += `"${r.region_name}",${r.batches},${r.hospitals},${r.amount_paid}\n`); } const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' }); const link = document.createElement('a'); link.download = `${this.reportFilters.type}_financial_report_${this.reportFilters.year}.csv`; link.href = URL.createObjectURL(blob); document.body.appendChild(link); link.click(); document.body.removeChild(link); this.showAlert('success', 'Report exported successfully'); } catch (e) { console.error("Failed to export report:", e); this.showAlert('failed', 'Failed to export report'); } },
    initializeCharts() { this.loadChartData().then(() => { const sCtx = document.getElementById('settlementChart'); if (sCtx) { this.settlementChart = new Chart(sCtx, { type: 'line', data: { labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], datasets: [{ label: 'Settlement Amount (ETB)', data: this.settlementChartData || new Array(12).fill(0), borderColor: '#556ee6', backgroundColor: 'rgba(85, 110, 230, 0.1)', tension: 0.4 }] }, options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true, ticks: { callback: (v) => 'ETB ' + v.toLocaleString() } } } } }); } const hCtx = document.getElementById('hospitalChart'); if (hCtx) { this.hospitalChart = new Chart(hCtx, { type: 'doughnut', data: { labels: this.hospitalChartLabels || ['No Data'], datasets: [{ data: this.hospitalChartData || [1], backgroundColor: ['#556ee6', '#34c38f', '#f46a6a', '#50a5f1', '#f1b44c', '#6c757d'] }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } } }); } }); },
    async loadChartData() { try { this.settlementChartData = await this.getMonthlySettlementData(); const hospitalData = await this.getHospitalPaymentData(); this.hospitalChartLabels = hospitalData.labels; this.hospitalChartData = hospitalData.data; } catch (error) { console.error('Failed to load chart data:', error); } },
    async getMonthlySettlementData() { try { const res = await executeJson({ "function": "getMonthlySettlementSummary", "year": new Date().getFullYear() }, authUrl); if (res.data.success && res.data.data?.monthlyData) { const monthlyData = new Array(12).fill(0); res.data.data.monthlyData.forEach(item => { if (item.amount !== null) monthlyData[parseInt(item.month) - 1] = parseFloat(item.amount); }); return monthlyData; } } catch (e) { console.error("Failed to load monthly data:", e); } return new Array(12).fill(0); },
    async getHospitalPaymentData() { try { const res = await executeJson({ "function": "getHospitalPaymentDistribution", "year": new Date().getFullYear() }, authUrl); if (res.data.success && res.data.data?.hospitalData?.length > 0) { return { labels: res.data.data.hospitalData.map(h => h.hospital_name), data: res.data.data.hospitalData.map(h => parseFloat(h.total_amount)) }; } } catch (e) { console.error("Failed to load hospital data:", e); } return { labels: ['No Data Available'], data: [1] }; },
    initializeTrendChart() { const ctx = document.getElementById('trendChart'); if (!ctx || !this.reportData.trendData) return; if (this.trendChart) this.trendChart.destroy(); const labels = this.reportData.trendData.map(item => { const [year, month] = item.month.split('-'); return this.months[parseInt(month) - 1] + ' ' + year; }); const data = this.reportData.trendData.map(item => parseFloat(item.monthly_amount || 0)); this.trendChart = new Chart(ctx, { type: 'bar', data: { labels, datasets: [{ label: 'Monthly Payments (ETB)', data, backgroundColor: '#556ee6', borderWidth: 1 }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { callback: (v) => 'ETB ' + v.toLocaleString() } } } } }); },
    formatAmount(amount) { return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0); },
    formatNumber(num) { return new Intl.NumberFormat('en-US').format(num || 0); },
    formatCurrency(amount) { return 'ETB ' + this.formatAmount(amount); },
    getStatusLabel(status) { const labels = { 'pending': 'Pending', 'processed': 'Processed', 'paid': 'Paid', 'cancelled': 'Cancelled' }; return labels[status] || status; },
    getStatusBadge(status) { const badges = { 'pending': 'badge bg-warning', 'processed': 'badge bg-info', 'paid': 'badge bg-success', 'cancelled': 'badge bg-danger' }; return badges[status] || 'badge bg-secondary'; },
    getReconciliationStatusBadge(status) { const badges = { 'complete': 'badge bg-success', 'processing': 'badge bg-warning', 'failed': 'badge bg-danger' }; return badges[status] || 'badge bg-secondary'; },
    getMatchRate(reconciliation) { if (!reconciliation.total_records) return 0; return Math.round((reconciliation.matched_records / reconciliation.total_records) * 100); },
    getMatchRateClass(reconciliation) { const rate = this.getMatchRate(reconciliation); if (rate >= 90) return 'bg-success'; if (rate >= 70) return 'bg-warning'; return 'bg-danger'; },
    clearAlerts() { this.alert.success = false; this.alert.info = false; this.alert.failed = false; },
    showAlert(type, message) { this.clearAlerts(); this.alert[type] = true; this.message = message; if (type === 'success' || type === 'info') setTimeout(() => this.clearAlerts(), 5000); }
  }
};
</script>

<style scoped>
.spinner-border { width: 2rem; height: 2rem; }
.search-icon { position: absolute; top: 50%; right: 10px; transform: translateY(-50%); color: #999; }
.nav-tabs-custom .nav-item .nav-link { color: #495057; font-weight: 500; }
.nav-tabs-custom .nav-item .nav-link.active { color: #556ee6; border-bottom: 2px solid #556ee6; }
.progress { background-color: #e9ecef; }
.form-check-input { cursor: pointer; }
.filter-tool-bar-btns .btn-group { margin-right: 5px; }
canvas { max-height: 300px; }
.report-content { animation: fadeIn 0.3s ease-in; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.gap-2 { gap: 0.5rem; }
.alert { min-width: 300px; box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15); }
.btn-check:checked + .btn-outline-primary { background-color: #0d6efd; color: white; }
.btn-check:checked + .btn-outline-success { background-color: #198754; color: white; }
.mini-stats-wid { border-left: 3px solid transparent; transition: all 0.3s ease; border-radius: 8px; }
.mini-stats-wid:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
</style>